import { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  buildMegaventoryInsertMvProductDraft,
  classifyMegaventoryInsertSku,
  isMegaventoryInsertDraftReady,
} from "./bomMegaventoryProductCategorize";

const PAGE_HOVER_HINT =
  "Carries BoM payloads from hub rules (process_boms depth zero, parity with Appsmith BoM Manager). Megaventory rejects lines without product IDs synced on public.sku.mv_id; this page lists those skips but does not automatically repair them.";

type ApprovedTemplateRow = {
  id: number;
  name: string;
  description: string | null;
  revision: number;
  revision_description: string | null;
  approved_at: string | null;
  mv_last_push: string | null;
};

function hoverHintText(value: string | null | undefined, emptyLabel: string): string {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : emptyLabel;
}

type BomExplodeRow = {
  finished_good_sku: string;
  finished_good_id: number | null;
  finished_good_sku_canonical?: string | null;
  component_sku: string | null;
  component_id: number | null;
  component_sku_canonical?: string | null;
  quantity: unknown;
  error_message?: string | null;
};

type PushProductGap = {
  sku: string;
  reason: "missing_in_megaventory" | "missing_local_mv_id" | "sku_case_mismatch";
  megaventorySku?: string;
  localSku?: string;
};

type GapCheckSummary = {
  finishedGoodsChecked: number;
  uniqueSkusChecked: number;
  totalPending: number;
};

type PushFinishedGoodRow = {
  sku: string;
  descriptionLabel: string;
  mvBomRevision: number | null;
  eligible: boolean;
};

function formatMvBomRevisionLabel(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return String(value);
}

function PushFinishedGoodsHoverHintTable(props: {
  rows: readonly PushFinishedGoodRow[];
  loading: boolean;
  hasSelection: boolean;
}): ReactElement {
  if (!props.hasSelection) {
    return (
      <span className="block max-w-sm text-slate-600">
        Select a template to see manufacture SKUs on it that already have a Megaventory product id, and which are eligible for push.
      </span>
    );
  }
  if (props.loading) {
    return <span className="block text-slate-600">Loading finished goods…</span>;
  }
  if (props.rows.length === 0) {
    return (
      <span className="block max-w-sm text-slate-600">
        No manufacture finished goods with a Megaventory product id are linked to this template.
      </span>
    );
  }
  return (
    <table className="min-w-[28rem] border-collapse text-left text-[11px] leading-snug">
      <thead>
        <tr className="border-b border-slate-200 text-slate-500">
          <th className="pr-3 pb-1 font-medium">SKU</th>
          <th className="pr-3 pb-1 font-medium">Description</th>
          <th className="pb-1 font-medium">BoM rev</th>
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <tr key={row.sku} className={row.eligible ? "text-green-700" : "text-red-700"}>
            <td className="pr-3 py-0.5 align-top whitespace-nowrap font-medium">{row.sku}</td>
            <td className="max-w-[14rem] pr-3 py-0.5 align-top break-words">{row.descriptionLabel}</td>
            <td className="py-0.5 align-top whitespace-nowrap tabular-nums">
              {formatMvBomRevisionLabel(row.mvBomRevision)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type MvProductBOMPayload = {
  ProductID: number | null;
  ProductSKU: string;
  mvRawMaterials: Array<{
    ProductID: number | null;
    ProductSKU: string;
    RawMaterialQuantity: string;
  }>;
};

function formatIso(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "—";
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function resolveCanonicalSku(bomSku: string, canonicalSku: string | null | undefined): string {
  const canonical = canonicalSku?.trim() ?? "";
  return canonical.length > 0 ? canonical : bomSku.trim();
}

function groupMegaventoryBoms(bomArray: readonly BomExplodeRow[]): MvProductBOMPayload[] {
  type GroupAccumulator = Omit<MvProductBOMPayload, "mvRawMaterials"> & {
    mvRawMaterials: MvProductBOMPayload["mvRawMaterials"];
  };
  const grouped: Record<string, GroupAccumulator> = {};
  for (const row of bomArray) {
    const finished_good_sku = resolveCanonicalSku(row.finished_good_sku ?? "", row.finished_good_sku_canonical);
    if (!grouped[finished_good_sku]) {
      grouped[finished_good_sku] = {
        ProductID: row.finished_good_id ?? null,
        ProductSKU: finished_good_sku,
        mvRawMaterials: [],
      };
    }
    const component_sku = resolveCanonicalSku(row.component_sku ?? "", row.component_sku_canonical);
    if (component_sku === "") {
      continue;
    }
    grouped[finished_good_sku].mvRawMaterials.push({
      ProductID: row.component_id ?? null,
      ProductSKU: component_sku,
      RawMaterialQuantity: String(row.quantity ?? ""),
    });
  }
  return Object.values(grouped);
}

function gapReasonLabel(gap: PushProductGap): string {
  if (gap.reason === "missing_in_megaventory") {
    return "Not in Megaventory — insert product";
  }
  if (gap.reason === "missing_local_mv_id") {
    return gap.megaventorySku
      ? `Megaventory has ${gap.megaventorySku}; run item ingest to link public.sku.mv_id`
      : "Megaventory product exists but public.sku.mv_id is missing — run item ingest";
  }
  return gap.localSku
    ? `BoM casing ${gap.sku} does not match linked SKU ${gap.localSku} — fix BoM rule casing`
    : "BoM SKU casing does not match public.sku — fix BoM rule casing";
}

function insertableGapSkus(gaps: readonly PushProductGap[]): string[] {
  return gaps.filter((gap) => gap.reason === "missing_in_megaventory").map((gap) => gap.sku);
}

function formatMegaventoryPushIssues(productSku: string, statusLabel: string, lines: readonly string[]): string {
  const body = lines.map((item) => `— ${item}`).join("\n");
  return `${productSku}: ${statusLabel}\n${body}`;
}

function formatApiErrorPlain(error: unknown): string {
  if (error instanceof ApiError && error.body !== undefined && error.body !== null) {
    if (typeof error.body === "object" && !Array.isArray(error.body)) {
      const typed = error.body as Record<string, unknown>;
      const problems = typed.problems;
      if (typed.status === "blocked" && Array.isArray(problems)) {
        return formatMegaventoryPushIssues(
          String(typed.productSku ?? "SKU"),
          "blocked",
          problems.map(String),
        );
      }
      if (typed.status === "failed") {
        return formatMegaventoryPushIssues(
          String(typed.productSku ?? "SKU"),
          "failed",
          [String(typed.error ?? "Megaventory error")],
        );
      }
    }
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

async function mutateMarkLastPush(input: {
  templateId: number;
  revision: number;
}): Promise<void> {
  await apiFetch(`/api/bom/megaventory-push/mark-last-push`, {
    method: "POST",
    body: input,
  });
}

async function mutatePushBom(input: {
  revision: number;
  mvProductBOM: MvProductBOMPayload;
}): Promise<unknown> {
  return apiFetch<unknown>(`/api/bom/megaventory-push/push-bom`, {
    method: "POST",
    body: input,
  });
}

function setPushLogLinesNow(
  setter: Dispatch<SetStateAction<string[]>>,
  value: string[] | ((previous: string[]) => string[]),
): void {
  flushSync(() => {
    setter(value);
  });
}

function setPushProgressNow(
  setter: Dispatch<SetStateAction<{ current: number; total: number } | null>>,
  value: { current: number; total: number } | null,
): void {
  flushSync(() => {
    setter(value);
  });
}

function addInsertingSkus(current: ReadonlySet<string>, skus: readonly string[]): ReadonlySet<string> {
  const next = new Set(current);
  for (const sku of skus) {
    next.add(sku);
  }
  return next;
}

function removeInsertingSkus(current: ReadonlySet<string>, skus: readonly string[]): ReadonlySet<string> {
  const next = new Set(current);
  for (const sku of skus) {
    next.delete(sku);
  }
  return next;
}

export function PushToMegaventoryPage(): ReactElement {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useHubModuleWrite("bom");
  const bomReadOnlyTitle =
    "Your role has read-only access to the BoM management hub module. Push runs require read/write.";

  const [selectedTemplate, setSelectedTemplate] = useState<ApprovedTemplateRow | null>(null);
  const [pushLogLines, setPushLogLines] = useState<string[]>([]);
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushRunning, setPushRunning] = useState(false);
  const [pushProgressSummary, setPushProgressSummary] = useState<{ current: number; total: number } | null>(null);
  const stopPushRequested = useRef(false);

  const [productGaps, setProductGaps] = useState<PushProductGap[]>([]);
  const [gapCheckSummary, setGapCheckSummary] = useState<GapCheckSummary | null>(null);
  const [missingCheckBusy, setMissingCheckBusy] = useState(false);
  const [insertingSkus, setInsertingSkus] = useState<ReadonlySet<string>>(() => new Set());

  const templatesQuery = useQuery({
    queryKey: ["bom", "megaventory-push", "templates"],
    queryFn: () => apiFetch<{ templates: ApprovedTemplateRow[] }>(`/api/bom/megaventory-push/approved-templates`),
    staleTime: 60_000,
  });

  const pendingSkusQuery = useQuery({
    queryKey: [
      "bom",
      "megaventory-push",
      "pending-skus",
      selectedTemplate?.id ?? null,
      selectedTemplate?.revision ?? null,
    ],
    queryFn: () => {
      const template = selectedTemplate;
      if (template == null) {
        throw new Error("No template selected.");
      }
      return apiFetch<{
        skus: string[];
        eligibleSkus: string[];
        finishedGoods: PushFinishedGoodRow[];
        totalPending: number;
      }>(`/api/bom/megaventory-push/pending-skus`, {
        method: "POST",
        body: {
          templateId: template.id,
          revision: template.revision,
          limit: 600,
        },
      });
    },
    enabled: selectedTemplate != null,
  });

  const bomLinesMutation = useMutation({
    mutationFn: (skus: string[]) =>
      apiFetch<{ rows: BomExplodeRow[] }>(`/api/bom/megaventory-push/bom-lines`, {
        method: "POST",
        body: { skus, cacheResults: false, depthLimit: 0 },
      }),
  });

  const insertProductsMutation = useMutation({
    mutationFn: (products: Array<Record<string, unknown>>) =>
      apiFetch<{ successes: Array<{ sku: string }>; failures: Array<{ sku: string; error: string }> }>(
        `/api/bom/megaventory-push/insert-products`,
        { method: "POST", body: { products } },
      ),
  });

  const selectedLabel = selectedTemplate ? `${selectedTemplate.name} · revision ${selectedTemplate.revision}` : "—";

  const templates = templatesQuery.data?.templates ?? [];

  function selectTemplate(row: ApprovedTemplateRow): void {
    setSelectedTemplate(row);
    setProductGaps([]);
    setGapCheckSummary(null);
    setMissingCheckBusy(false);
    setInsertingSkus(new Set());
  }

  const lastPendingLabel = useMemo(() => {
    if (selectedTemplate == null) {
      return "Select a template above to load eligible finished-good counts.";
    }
    if (pendingSkusQuery.isFetching && pendingSkusQuery.data == null) {
      return "Loading eligible finished-good counts…";
    }
    const total = pendingSkusQuery.data?.totalPending;
    if (typeof total !== "number") {
      return "Could not load eligible finished-good counts.";
    }
    const batch = pendingSkusQuery.data?.skus?.length ?? 0;
    return `${formatNumberUk(total)} finished goods eligible · this batch sampled ${formatNumberUk(batch)}`;
  }, [pendingSkusQuery.data, pendingSkusQuery.isFetching, selectedTemplate]);

  const eligibleFinishedGoodsHoverHint = useMemo((): ReactNode => {
    return (
      <PushFinishedGoodsHoverHintTable
        rows={pendingSkusQuery.data?.finishedGoods ?? []}
        loading={pendingSkusQuery.isFetching && pendingSkusQuery.data == null}
        hasSelection={selectedTemplate != null}
      />
    );
  }, [pendingSkusQuery.data, pendingSkusQuery.isFetching, selectedTemplate]);

  async function collectMissing(): Promise<void> {
    if (selectedTemplate == null) return;
    if (!hubWritesAllowed) return;

    setMissingCheckBusy(true);

    try {
      setProductGaps([]);
      setGapCheckSummary(null);
      const response = await apiFetch<{
        gaps: PushProductGap[];
        totalPending: number;
        finishedGoodsChecked: number;
        uniqueSkusChecked: number;
      }>(`/api/bom/megaventory-push/gap-check`, {
        method: "POST",
        body: {
          templateId: selectedTemplate.id,
          revision: selectedTemplate.revision,
        },
      });
      setProductGaps(response.gaps ?? []);
      setGapCheckSummary({
        finishedGoodsChecked: response.finishedGoodsChecked ?? 0,
        uniqueSkusChecked: response.uniqueSkusChecked ?? 0,
        totalPending: response.totalPending ?? 0,
      });

      queryClient.invalidateQueries({
        queryKey: ["bom", "megaventory-push"],
      });
    } catch (error: unknown) {
      setProductGaps([]);
      setGapCheckSummary(null);
      const message =
        error instanceof ApiError ? formatApiErrorPlain(error) : error instanceof Error ? error.message : String(error);
      alert(`Push gap check failed:\n${message}`);
    } finally {
      setMissingCheckBusy(false);
    }
  }

  async function runPushBatch(): Promise<void> {
    if (!hubWritesAllowed) return;
    const tmpl = selectedTemplate;
    if (tmpl == null) return;
    stopPushRequested.current = false;
    flushSync(() => {
      setPushModalOpen(true);
      setPushRunning(true);
      setPushProgressSummary(null);
      setPushLogLines(["Starting Megaventory push batch…"]);
    });

    try {
      setPushLogLinesNow(setPushLogLines, (previous) => [
        ...previous,
        "Loading pending finished-good batch…",
      ]);

      const pendingResult = await pendingSkusQuery.refetch();
      const pendingResponse = pendingResult.data;
      if (pendingResponse == null) {
        setPushLogLinesNow(setPushLogLines, ["Could not load the pending finished-good batch."]);
        setPushProgressNow(setPushProgressSummary, null);
        return;
      }
      const skuArray = pendingResponse.skus ?? [];
      if (!Array.isArray(skuArray) || skuArray.length === 0) {
        const idleLines =
          pendingResponse.totalPending > 0
            ? [
                `No SKU batch sampled while ${formatNumberUk(pendingResponse.totalPending)} finished goods remain in the backlog. Select the template again to resample.`,
              ]
            : ["No manufactured finished goods locally require this BOM revision."];
        if (pendingResponse.totalPending === 0) {
          await mutateMarkLastPush({
            templateId: tmpl.id,
            revision: tmpl.revision,
          });
          idleLines.push(
            "Recorded Megaventory last push stamp on this approved revision.",
          );
          queryClient.invalidateQueries({ queryKey: ["bom", "megaventory-push"] });
        } else {
          idleLines.push(
            "Eligible rows must match template, manufacture=true, BOM revision mismatch, and for-sale/not-for-sale gating mirrored from Appsmith.",
          );
        }
        setPushProgressNow(setPushProgressSummary, null);
        setPushLogLinesNow(setPushLogLines, idleLines);
        return;
      }

      setPushLogLinesNow(setPushLogLines, (previous) => [
        ...previous,
        `Batch has ${formatNumberUk(skuArray.length)} finished good(s) (${formatNumberUk(pendingResponse.totalPending)} pending on this revision).`,
        `Exploding BOMs (process_boms)…`,
      ]);

      const explodedResponse = await bomLinesMutation.mutateAsync(skuArray);
      const bomLinesRaw = explodedResponse.rows ?? [];
      const fetchErrors = bomLinesRaw
        .filter(
          (row) =>
            row.error_message != null &&
            String(row.error_message).trim().length > 0,
        )
        .map((row) =>
          `${row.finished_good_sku}: ${String(row.error_message)}`,
        );
      const groupedPayloads = groupMegaventoryBoms(bomLinesRaw);

      let localLog = [
        ...fetchErrors,
        `Exploded ${formatNumberUk(bomLinesRaw.length)} BOM line(s) into ${formatNumberUk(groupedPayloads.length)} Megaventory envelope(s).`,
        "Pushing to Megaventory…",
      ];
      setPushLogLinesNow(setPushLogLines, localLog);

      const total = groupedPayloads.length;
      setPushProgressNow(setPushProgressSummary, { current: 0, total });

      let blockedOrFailedDuringRun = false;

      const revisionToMark = tmpl.revision;

      for (let fgIndex = 0; fgIndex < groupedPayloads.length; fgIndex += 1) {
        if (stopPushRequested.current) {
          localLog = [...localLog, "Operator requested stop before the next finished good."];
          setPushLogLinesNow(setPushLogLines, localLog);
          setPushProgressNow(setPushProgressSummary, null);
          return;
        }

        const fg = groupedPayloads[fgIndex]!;
        setPushProgressNow(setPushProgressSummary, { current: fgIndex + 1, total });
        setPushLogLinesNow(setPushLogLines, (previous) => [
          ...previous,
          `Pushing ${formatNumberUk(fgIndex + 1)} of ${formatNumberUk(total)}: ${fg.ProductSKU}…`,
        ]);

        try {
          await mutatePushBom({
            revision: revisionToMark,
            mvProductBOM: fg,
          });
          localLog = [...localLog, `OK pushed BOM for ${fg.ProductSKU}`];
          setPushLogLinesNow(setPushLogLines, localLog);
        } catch (error: unknown) {
          blockedOrFailedDuringRun = true;
          localLog = [...localLog, formatApiErrorPlain(error)];
          setPushLogLinesNow(setPushLogLines, localLog);
        }
      }

      setPushLogLinesNow(setPushLogLines, (previous) => [...previous, "Checking remaining queue…"]);

      const remainingProbe = await apiFetch<{ skus: string[]; totalPending: number }>(
        `/api/bom/megaventory-push/pending-skus`,
        {
          method: "POST",
          body: {
            templateId: tmpl.id,
            revision: revisionToMark,
            limit: 600,
          },
        },
      );

      if (!blockedOrFailedDuringRun && remainingProbe.totalPending === 0 && remainingProbe.skus.length === 0) {
        await mutateMarkLastPush({
          templateId: tmpl.id,
          revision: revisionToMark,
        });
        localLog = [
          ...localLog,
          "Queue empty for this revision and batch had no skips. Stored template revision Megaventory last push stamp.",
        ];
        queryClient.invalidateQueries({ queryKey: ["bom", "megaventory-push"] });
      } else if (blockedOrFailedDuringRun) {
        localLog = [
          ...localLog,
          remainingProbe.totalPending > 0
            ? `Resolve errors, run item ingest if new SKUs were created, then rerun. Still ${formatNumberUk(remainingProbe.totalPending)} SKU rows behind this BOM revision locally.`
            : "Stopped after failures; rerun only after fixing issues.",
        ];
      } else {
        localLog = [
          ...localLog,
          `Batch processed. Approximately ${formatNumberUk(remainingProbe.totalPending)} finished goods remain on this revision. Run push again.`,
        ];
      }

      setPushLogLinesNow(setPushLogLines, localLog);
      await pendingSkusQuery.refetch();
    } catch (error: unknown) {
      setPushLogLinesNow(setPushLogLines, [
        ...(error instanceof Error ? [error.message] : [String(error)]),
      ]);
      setPushProgressNow(setPushProgressSummary, null);
    } finally {
      setPushRunning(false);
    }
  }

  async function insertMissingSkus(skus: readonly string[]): Promise<void> {
    if (!hubWritesAllowed) return;
    const dedupedSkus = [...new Set(skus.map((sku) => sku.trim()).filter(Boolean))];
    if (dedupedSkus.length === 0) return;

    const drafts = dedupedSkus.map((sku) => buildMegaventoryInsertMvProductDraft(sku));
    const blockedSkus = dedupedSkus.filter((_sku, index) => !isMegaventoryInsertDraftReady(drafts[index]!));
    if (blockedSkus.length > 0) {
      showToast({
        tone: "error",
        message: `Cannot insert ${formatNumberUk(blockedSkus.length)} SKU row(s): Megaventory requires a non-empty product description. ${blockedSkus.join(", ")}`,
      });
      return;
    }

    setInsertingSkus((current) => addInsertingSkus(current, dedupedSkus));

    try {
      const fallbackSkus = dedupedSkus.filter((sku) => classifyMegaventoryInsertSku(sku) === "fallback");
      const result = await insertProductsMutation.mutateAsync(drafts);
      const successes = result.successes ?? [];
      const failures = result.failures ?? [];
      const insertedSkuKeys = new Set(successes.map((row) => skuKeyNormalize(row.sku)));

      setProductGaps((previous) =>
        previous.filter((gap) => !insertedSkuKeys.has(skuKeyNormalize(gap.sku))),
      );

      if (failures.length > 0) {
        console.info("Megaventory insert batch result", result);
      }

      if (failures.length > 0) {
        const failureSummary = failures.map((row) => `${row.sku}: ${row.error}`).join(" · ");
        showToast({
          tone: "error",
          message: `Inserted ${formatNumberUk(successes.length)} SKU row(s); ${formatNumberUk(failures.length)} failed. ${failureSummary}`,
        });
      } else if (dedupedSkus.length === 1) {
        showToast({
          tone: "success",
          message: `Inserted ${dedupedSkus[0]!} into Megaventory.`,
        });
      } else {
        showToast({
          tone: "success",
          message: `Inserted ${formatNumberUk(successes.length)} SKU row(s) into Megaventory.`,
        });
      }

      if (fallbackSkus.length > 0) {
        showToast({
          tone: "info",
          message: `${formatNumberUk(fallbackSkus.length)} SKU row(s) used generic fallback defaults (description = SKU). Review in Megaventory after ingest.`,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["bom", "megaventory-push"] });
    } catch (error: unknown) {
      const message =
        error instanceof ApiError ? formatApiErrorPlain(error) : error instanceof Error ? error.message : String(error);
      showToast({ tone: "error", message: `Insert failed: ${message}` });
    } finally {
      setInsertingSkus((current) => removeInsertingSkus(current, dedupedSkus));
    }
  }

  const insertableSkus = useMemo(() => insertableGapSkus(productGaps), [productGaps]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-5 py-6">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Push to Megaventory</PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Approved BOM templates</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {templatesQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading catalog…</p>
          ) : templatesQuery.isError ? (
            <p className="text-sm text-rose-700">{(templatesQuery.error as Error).message}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 bg-white text-left text-xs text-slate-800">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="border border-slate-200 px-2 py-1.5 font-medium">BoM template</th>
                    <th className="border border-slate-200 px-2 py-1.5 font-medium">Approved revision</th>
                    <th className="border border-slate-200 px-2 py-1.5 font-medium">Last push stamp</th>
                    <th className="border border-slate-200 px-2 py-1.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((row) => {
                    const picked =
                      selectedTemplate?.id === row.id && selectedTemplate.revision === row.revision;
                    return (
                      <tr
                        key={`${String(row.id)}-${String(row.revision)}`}
                        className={cn(
                          "cursor-pointer transition-colors hover:bg-slate-50",
                          picked && "bg-emerald-50 hover:bg-emerald-50",
                        )}
                        tabIndex={0}
                        role="button"
                        aria-label={`Select ${row.name} revision ${String(row.revision)}`}
                        aria-pressed={picked}
                        onClick={() => selectTemplate(row)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            selectTemplate(row);
                          }
                        }}
                      >
                        <td className="border border-slate-200 px-2 py-1 align-top">
                          <HoverHintContent
                            hoverHint={hoverHintText(row.description, "No template description recorded.")}
                            block
                            cursor="default"
                            className="max-w-full"
                          >
                            <span className="block max-w-full">{row.name}</span>
                          </HoverHintContent>
                        </td>
                        <td className="border border-slate-200 px-2 py-1 align-top">
                          <HoverHintContent
                            hoverHint={hoverHintText(
                              row.revision_description,
                              "No revision notes recorded for this approved revision.",
                            )}
                            block
                            cursor="default"
                            className="max-w-full"
                          >
                            <span className="block max-w-full">{row.revision}</span>
                          </HoverHintContent>
                        </td>
                        <td className="border border-slate-200 px-2 py-1 align-top">
                          {formatIso(row.mv_last_push)}
                        </td>
                        <td className="border border-slate-200 px-2 py-1">
                          <Button
                            variant="ghost"
                            className="h-7 px-2 text-[11px]"
                            type="button"
                            disabled={templatesQuery.isLoading}
                            onClick={(event) => {
                              event.stopPropagation();
                              selectTemplate(row);
                            }}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Operate on selection</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4 text-sm leading-relaxed text-slate-700">
          <p>
            Selected template:&nbsp;<span className="font-semibold">{selectedLabel}</span>
          </p>
          <HoverHintContent
            hoverHint={eligibleFinishedGoodsHoverHint}
            block
            cursor="default"
            className="max-w-full"
          >
            <p className="text-xs text-slate-600">{lastPendingLabel}</p>
          </HoverHintContent>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={selectedTemplate == null || !hubWritesAllowed || missingCheckBusy}
              onClick={() => void collectMissing()}
              title={!hubWritesAllowed ? bomReadOnlyTitle : undefined}
            >
              <StableButtonLabel
                label="Check push gaps"
                busyLabel="Checking…"
                isBusy={missingCheckBusy}
              />
            </Button>

            <Button
              type="button"
              disabled={selectedTemplate == null || bomLinesMutation.isPending || !hubWritesAllowed}
              onClick={() => void runPushBatch()}
              title={!hubWritesAllowed ? bomReadOnlyTitle : undefined}
            >
              Push BoM batch
            </Button>
          </div>

          {gapCheckSummary != null ? (
            <p className="text-xs text-slate-600">
              Checked {formatNumberUk(gapCheckSummary.finishedGoodsChecked)} eligible finished goods (
              {formatNumberUk(gapCheckSummary.uniqueSkusChecked)} unique BoM SKUs) ·{" "}
              {formatNumberUk(gapCheckSummary.totalPending)} total pending on this revision
            </p>
          ) : null}

          {productGaps.length > 0 && (
            <div className="space-y-3 rounded border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
              <p className="font-semibold">Push blockers (Megaventory + public.sku linkage)</p>
              <MissingSkuGapList
                title="All eligible finished goods and expanded BoM"
                gaps={productGaps}
                insertableSkus={insertableSkus}
                hubWritesAllowed={hubWritesAllowed}
                bomReadOnlyTitle={bomReadOnlyTitle}
                insertingSkus={insertingSkus}
                onInsertAll={(skus) => void insertMissingSkus(skus)}
                onInsertOne={(sku) => void insertMissingSkus([sku])}
              />
              <p className="text-xs text-slate-600">
                Gap check scans every eligible finished good for this template, not a random push batch. Product
                inserts apply only when Megaventory has no product yet; casing mismatches need a BoM rule fix.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {pushModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
          <Card className="w-full max-w-lg shadow-lg">
            <CardBody className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">Megaventory push progress</h2>
              <p className="text-xs text-slate-500">
                {pushProgressSummary
                  ? `${formatNumberUk(pushProgressSummary.current)} of ${formatNumberUk(pushProgressSummary.total)} finished-good envelopes pushed`
                  : pushRunning
                    ? "Preparing batch or exploding BOMs…"
                    : "Finished"}
              </p>
              <div className="max-h-[240px] space-y-1 overflow-y-auto rounded border border-slate-200 bg-slate-950 p-2 font-mono text-[11px] text-emerald-200">
                {pushLogLines.length === 0 ? (
                  <p className="text-slate-500">Starting…</p>
                ) : (
                  pushLogLines.map((line, index) => <div key={`${String(index)}-${line.slice(0, 20)}`}>{line}</div>)
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (pushRunning) {
                      stopPushRequested.current = true;
                    }
                    setPushModalOpen(false);
                  }}
                >
                  {pushRunning ? "Stop and close console" : "Done"}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function formatNumberUk(n: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

function skuKeyNormalize(sku: string): string {
  return sku.trim().toLowerCase();
}

function MissingSkuGapList({
  title,
  gaps,
  insertableSkus,
  hubWritesAllowed,
  bomReadOnlyTitle,
  insertingSkus,
  onInsertAll,
  onInsertOne,
}: {
  title: string;
  gaps: readonly PushProductGap[];
  insertableSkus: readonly string[];
  hubWritesAllowed: boolean;
  bomReadOnlyTitle: string;
  insertingSkus: ReadonlySet<string>;
  onInsertAll: (skus: readonly string[]) => void;
  onInsertOne: (sku: string) => void;
}): ReactElement {
  const insertAllBusy =
    insertableSkus.length > 0 && insertableSkus.every((sku) => insertingSkus.has(sku));
  const anyInsertInFlight = insertingSkus.size > 0;

  return (
    <div>
      <p className="text-xs uppercase tracking-wide">{title}</p>
      {insertableSkus.length > 0 ? (
        <div className="mt-2">
          <Button
            type="button"
            size="sm"
            disabled={!hubWritesAllowed || anyInsertInFlight}
            title={!hubWritesAllowed ? bomReadOnlyTitle : undefined}
            onClick={() => onInsertAll(insertableSkus)}
          >
            <StableButtonLabel label="Insert all" busyLabel="Inserting all…" isBusy={insertAllBusy} />
          </Button>
        </div>
      ) : null}
      <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto">
        {gaps.map((gap) => {
          const rowBusy = insertingSkus.has(gap.sku);
          const canInsert = gap.reason === "missing_in_megaventory";
          return (
            <li key={gap.sku} className="flex items-start gap-2">
              {canInsert ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="shrink-0"
                  disabled={!hubWritesAllowed || rowBusy}
                  title={!hubWritesAllowed ? bomReadOnlyTitle : undefined}
                  onClick={() => onInsertOne(gap.sku)}
                >
                  <StableButtonLabel label="Insert" busyLabel="Inserting…" isBusy={rowBusy} />
                </Button>
              ) : (
                <span className="inline-flex w-[4.5rem] shrink-0 justify-center text-[10px] uppercase tracking-wide text-amber-800">
                  Fix
                </span>
              )}
              <div className="min-w-0">
                <div className="font-mono text-xs">{gap.sku}</div>
                <div className="text-[11px] leading-snug text-amber-950/80">{gapReasonLabel(gap)}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StableButtonLabel({
  label,
  busyLabel,
  isBusy,
}: {
  label: string;
  busyLabel: string;
  isBusy: boolean;
}): ReactElement {
  return (
    <span className="inline-grid grid-cols-1 grid-rows-1">
      <span className="invisible col-start-1 row-start-1">{label}</span>
      <span className="col-start-1 row-start-1">{isBusy ? busyLabel : label}</span>
    </span>
  );
}
